import useVehicleSelectionSparesbox from '@shared/composables/useVehicleSelectionSparesbox';
import { IUserSelectedVehicle } from '@shared/types/IVehicleSelection';
import {
  UserVehicleSelectionKey,
  useVehicleSearch,
} from '@unified-commerce/gpc-vue-storefront-search';
import Cookies from 'js-cookie';

const useMyGarageSparesbox = () => {
  const { setUserSelectedVehicle, setVehiclesInGarage } = useVehicleSelectionSparesbox();
  const { search: vehicleFilterSearch, result: vehicleFilterSearchResult } =
    useVehicleSearch('vehicle-filter-search');

  const addVehicle = async (vehicleToAdd: IUserSelectedVehicle) => {
    const updatedVehicle = { ...vehicleToAdd, selected: true };
    const vehiclesInGarage = await getAndResetVehicles(true);

    const index = vehiclesInGarage.findIndex((v) => v.KtypNr === updatedVehicle.KtypNr);

    if (index === -1) {
      vehiclesInGarage.push(updatedVehicle);
    } else {
      vehiclesInGarage[index].selected = true;
    }

    const serialized = JSON.stringify(vehiclesInGarage);

    setVehicleSelectionsInCookie(serialized);
    setVehiclesSelectionsInLs(serialized);
    setUserSelectedVehicle(updatedVehicle);
    setVehiclesInGarage(vehiclesInGarage);
  };

  const setVehiclesSelectionsInLs = (selections: string) => {
    if (typeof window != 'undefined') {
      window.localStorage.setItem(UserVehicleSelectionKey, selections);
    }
  };

  const setVehicleSelectionsInCookie = (selections: string): Promise<unknown> => {
    return Promise.resolve(Cookies.set(UserVehicleSelectionKey, selections, { expires: 365 * 2 }));
  };

  const getAllVehicleSelectionsFromLs = async (): Promise<IUserSelectedVehicle[] | null> => {
    if (typeof window === 'undefined') return null;

    const rawLocalStorage = window.localStorage.getItem(UserVehicleSelectionKey);
    if (!rawLocalStorage) return null;

    try {
      const parsedLocalStorage = JSON.parse(rawLocalStorage);
      if (!Array.isArray(parsedLocalStorage)) {
        console.warn('parsed vehicle data not in expected shape.');
        return null;
      }

      const userSelectedVehicles: IUserSelectedVehicle[] = parsedLocalStorage;
      const hasMissingIds = userSelectedVehicles.some((v) => v.id == null && v.KtypNr != null);

      // if ids present in data, return data.
      if (!hasMissingIds) return userSelectedVehicles;
      // otherwise, look up their details and set to local storage.
      const updatedVehicles = await resolveMissingVehicleIds(userSelectedVehicles);
      const serialized = JSON.stringify(updatedVehicles);
      setVehiclesSelectionsInLs(serialized);
      return updatedVehicles;
    } catch (err) {
      console.error('Failed to parse vehicle selections from localStorage:', err);
      return null;
    }
  };

  const getVehicleSelectionFromCookie = async (): Promise<IUserSelectedVehicle | null> => {
    const userVehicleSelectionsString = Cookies.get(UserVehicleSelectionKey);

    if (!userVehicleSelectionsString) return null;

    try {
      const parsed = JSON.parse(userVehicleSelectionsString);
      if (!Array.isArray(parsed)) return null;

      const userSelectedVehicles: IUserSelectedVehicle[] = parsed;
      const hasMissingIds = userSelectedVehicles.some((v) => v.id == null && v.KtypNr != null);

      const finalVehicles = hasMissingIds
        ? await resolveMissingVehicleIds(userSelectedVehicles)
        : userSelectedVehicles;

      if (hasMissingIds) {
        Cookies.set(UserVehicleSelectionKey, JSON.stringify(finalVehicles), { expires: 365 * 2 });
      }

      return getUserSelectedVehicle(JSON.stringify(finalVehicles));
    } catch (err) {
      console.error('Failed to parse or process vehicle selections from cookie:', err);
      return null;
    }
  };

  const getUserSelectedVehicle = (userSelectedVehicleString: string): IUserSelectedVehicle => {
    const userVehicleSelections: IUserSelectedVehicle[] = JSON.parse(userSelectedVehicleString);
    const selectedUserVehicleSelection = userVehicleSelections.filter(
      (vehicle) => vehicle.selected,
    )[0];

    return selectedUserVehicleSelection;
  };

  const removeVehicleFromGarage = async (vehicleToRemove: IUserSelectedVehicle) => {
    const currentVehicles = await getAndResetVehicles(false);

    const vehiclesRemaining = currentVehicles.filter(
      (vehicle) => vehicle.KtypNr !== vehicleToRemove.KtypNr,
    );
    setVehicleSelectionsInCookie(JSON.stringify(vehiclesRemaining));
    setVehiclesSelectionsInLs(JSON.stringify(vehiclesRemaining));
    if (vehicleToRemove.selected) {
      setUserSelectedVehicle(null);
    }
    setVehiclesInGarage(vehiclesRemaining);
  };

  const resetVehiclesFromGarage = async () => {
    const currentVehiclesInGarage = await getAndResetVehicles(true);
    setVehicleSelectionsInCookie(JSON.stringify(currentVehiclesInGarage));
    setVehiclesSelectionsInLs(JSON.stringify(currentVehiclesInGarage));
    setUserSelectedVehicle(null);
    setVehiclesInGarage(currentVehiclesInGarage);
  };

  const getAndResetVehicles = async (shouldReset: boolean): Promise<IUserSelectedVehicle[]> => {
    const vehiclesInGarage = (await getAllVehicleSelectionsFromLs()) || [];

    if (!shouldReset) return vehiclesInGarage;

    return vehiclesInGarage.map((vehicle) => ({
      ...vehicle,
      selected: false,
    }));
  };

  const syncSelectionCookieWithLocalstorage = () => {
    if (typeof window != 'undefined') {
      const selectionInCookie = Cookies.get(UserVehicleSelectionKey);
      const selectionInLs = window.localStorage.getItem(UserVehicleSelectionKey);
      if (selectionInLs && !selectionInCookie) {
        setVehicleSelectionsInCookie(selectionInLs);
      }
    }
  };

  const resolveMissingVehicleIds = (
    vehicles: IUserSelectedVehicle[],
  ): Promise<IUserSelectedVehicle[]> => {
    return Promise.all(
      vehicles.map(async (vehicle) => {
        if (vehicle.id != null) return vehicle;

        if (!vehicle.KtypNr) {
          console.warn(`Vehicle is missing a valid KtypNr and cannot be resolved:`, vehicle);
          return vehicle;
        }

        try {
          await vehicleFilterSearch({
            resultsPerPage: 1,
            filters: `KtypNr:${vehicle.KtypNr}`,
          });

          const vehicleId = vehicleFilterSearchResult?.value?.results?.[0]?.hits?.[0]?.VehicleID;

          if (vehicleId) {
            return { ...vehicle, id: Number(vehicleId) };
          }
        } catch (err) {
          console.warn(`Failed to fetch vehicle data for KtypNr ${vehicle.KtypNr}`, err);
        }

        return vehicle;
      }),
    );
  };

  return {
    addVehicle,
    resetVehiclesFromGarage,
    removeVehicleFromGarage,
    getUserSelectedVehicle,
    getVehicleSelectionFromCookie,
    setVehicleSelectionsInCookie,
    setVehiclesSelectionsInLs,
    getAllVehicleSelectionsFromLs,
    syncSelectionCookieWithLocalstorage,
  };
};

export default useMyGarageSparesbox;
